export type TimeoutId = string & { readonly __brand: 'TimeoutId' };
export type TimeoutCallback = () => void | Promise<void>;

interface TimeoutEntry {
  readonly id: TimeoutId;
  readonly callback: TimeoutCallback;
  readonly delay: number;
  readonly createdAt: Date;
  nodeTimeout: NodeJS.Timeout | null;
}

export class TimeoutManager {
  private readonly timeouts: Map<TimeoutId, TimeoutEntry>;
  private idCounter: number = 0;

  constructor () {
    this.timeouts = new Map();
  }

  setTimeout (callback: TimeoutCallback, delay: number): TimeoutId {
    const id = this.generateId();

    const wrappedCallback = async (): Promise<void> => {
      try {
        await callback();
      } catch (error) {
        console.error('[TimeoutManager] Timeout callback error:', error);
      } finally {
        this.timeouts.delete(id);
      }
    };

    const nodeTimeout = setTimeout(wrappedCallback, delay);

    const entry: TimeoutEntry = {
      id,
      callback,
      delay,
      createdAt: new Date(),
      nodeTimeout,
    };

    this.timeouts.set(id, entry);

    return id;
  }

  clearTimeout (id: TimeoutId): boolean {
    const entry = this.timeouts.get(id);
    if (!entry) {
      return false;
    }

    if (entry.nodeTimeout) {
      clearTimeout(entry.nodeTimeout);
      entry.nodeTimeout = null;
    }

    this.timeouts.delete(id);
    return true;
  }

  clearAll (): void {
    for (const entry of this.timeouts.values()) {
      if (entry.nodeTimeout) {
        clearTimeout(entry.nodeTimeout);
      }
    }
    this.timeouts.clear();
  }

  private generateId (): TimeoutId {
    this.idCounter++;
    return `timeout_${this.idCounter}_${Date.now()}` as TimeoutId;
  }
}

export function createTimeoutManager (): TimeoutManager {
  return new TimeoutManager();
}
